import { AutoAwesome, BugReport } from '@mui/icons-material';
import {
  Box,
  Checkbox,
  Collapse,
  FormControlLabel,
  InputAdornment,
  TextField,
  Typography,
} from '@mui/material';
import React from 'react';

export const AI_FEATURES = [
  {
    id: 'testCaseGeneration',
    featureKey: 'testCaseGeneration',
    label: 'Test Case Generation',
    description: 'Generate test cases from a scenario description or from a Jira user story',
    icon: <AutoAwesome sx={{ fontSize: 16, color: 'primary.main' }} />,
    limits: [
      {
        field: 'maxPerRequest',
        apiField: 'maxResults',
        label: 'Max per request',
        hint: 'Max test cases generated in a single run',
        unit: 'test cases',
        min: 2,
        max: 25,
        defaultValue: 10,
        nullable: false,
      },
      {
        field: 'dailyRequestLimit',
        apiField: 'requestPerDay',
        label: 'Daily request limit',
        hint: 'Max AI calls per user per day.',
        unit: 'req / day',
        min: 1,
        max: 9999,
        defaultValue: null,
        nullable: true,
      },
    ],
  },
  {
    id: 'defectIntelligence',
    featureKey: 'defectIntelligence',
    label: 'Defect Intelligence',
    description: 'AI analysis to detect patterns and insights across defects',
    icon: <BugReport sx={{ fontSize: 16, color: 'primary.main' }} />,
    showInBoth: true,
  },
];

const AIFeatureCheckboxes = ({
  features = {},
  onChange,
  limits = {},
  onLimitChange,
  disabled = false,
  scope = 'project',
}) => {
  const visibleFeatures = AI_FEATURES.filter((f) => f.showInBoth || !!f.orgOnly === (scope === 'org'));
  const showLimits = typeof onLimitChange === 'function';

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
      {visibleFeatures.map((feature) => {
        const { id, label, description, icon } = feature;
        const isChecked = !!features[id];
        const featureLimits = limits[id] ?? {};

        return (
          <Box key={id}>
            <FormControlLabel
              disabled={disabled}
              control={
                <Checkbox
                  size="small"
                  checked={isChecked}
                  onChange={(e) => onChange?.(id, e.target.checked)}
                />
              }
              label={
                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.75, pt: 0.5 }}>
                  {icon}
                  <Box>
                    <Typography variant="body2" fontWeight={500}>{label}</Typography>
                    <Typography variant="caption" color="text.secondary">{description}</Typography>
                  </Box>
                </Box>
              }
              sx={{ alignItems: 'flex-start', mr: 0, mb: 0 }}
            />

            {showLimits && !!feature.limits?.length && (
              <Collapse in={isChecked} unmountOnExit>
                <Box
                  sx={{
                    ml: 3.5, mt: 0.75, mb: 1, p: 1.5,
                    bgcolor: 'background.default',
                    border: '0.5px solid', borderColor: 'divider',
                    borderRadius: 1.5,
                    display: 'flex', flexDirection: 'column', gap: 1.5,
                  }}
                >
                  {feature.limits.map((cfg) => (
                    <LimitField
                      key={cfg.field}
                      {...cfg}
                      value={
                        featureLimits[cfg.field] !== undefined
                          ? featureLimits[cfg.field]
                          : (cfg.defaultValue ?? (cfg.nullable ? null : ''))
                      }
                      onChange={(v) => onLimitChange(id, cfg.field, v)}
                      disabled={disabled}
                    />
                  ))}
                </Box>
              </Collapse>
            )}
          </Box>
        );
      })}
    </Box>
  );
};

const LimitField = ({ label, hint, value, unit, min, max, nullable, onChange, disabled }) => (
  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2 }}>
    <Box sx={{ flex: 1, minWidth: 0 }}>
      <Typography variant="caption" fontWeight={500} display="block">{label}</Typography>
      <Typography variant="caption" color="text.secondary">{hint}</Typography>
    </Box>
    <TextField
      size="small"
      type="number"
      value={value === null || value === undefined ? '' : value}
      placeholder={nullable ? 'No limit' : '—'}
      disabled={disabled}
      onChange={(e) => {
        const raw = e.target.value;
        if (raw === '') {
          onChange(nullable ? null : min);
        } else {
          onChange(Math.min(max, Math.max(min, Number(raw))));
        }
      }}
      inputProps={{
        min,
        max,
        style: { textAlign: 'right', width: 80, fontFamily: 'monospace', fontSize: 13 },
      }}
      InputProps={{
        endAdornment: (
          <InputAdornment position="end">
            <Typography variant="caption" color="text.secondary" noWrap>{unit}</Typography>
          </InputAdornment>
        ),
      }}
      sx={{ '& .MuiOutlinedInput-root': { borderRadius: 1.5 }, flexShrink: 0 ,width: 180}}
    />
  </Box>
);

export default AIFeatureCheckboxes;